/**
 * The Performance Dashboard contract — one flat router, `performanceDashboardContract`
 * (`/performance/dashboard`), exactly two GETs. See the Performance Dashboard plan
 * (`docs/plans/performance-management/performance-dashboard-plan.md`) for the full design.
 *
 * The Dashboard has no data model of its own — every field here is an aggregate read over Goals
 * (Section 2), Feedback/1:1s/Recognition (Section 3), Reviews (Section 4), and Insights
 * (Section 5, trend only). `blocks` is not optional: `null` meaning "you may not see this block"
 * and `null` meaning "there is nothing here yet" are different renders, and conflating them
 * produces an empty state that lies (e.g. "0 recognition points" shown to someone whose org has
 * recognition disabled for them). Callers MUST branch on `blocks.*Visible` before rendering an
 * empty state for a block.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { recognitionSchema } from './recognition.contract.js';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  paginatedSchema,
  paginationQuerySchema,
  percentSchema,
  ratingValueSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared local schemas
// ---------------------------------------------------------------------------

/**
 * A signed score delta ("+0.3 vs last cycle") — one decimal place, unlike `ratingValueSchema`
 * which is unsigned (a rating can never be negative, but a change in it can).
 */
export const signedScoreDeltaSchema = z
  .string()
  .regex(/^-?\d{1,2}(\.\d)?$/, 'Must be a decimal with at most one place');

/** A signed percentage ("+1.2%" quarterly growth) — `percentSchema`'s signed sibling. */
export const signedPercentSchema = z
  .string()
  .regex(/^-?\d{1,3}(\.\d{1,2})?$/, 'Must be a decimal with at most two places');

// ---------------------------------------------------------------------------
// getOverview
// ---------------------------------------------------------------------------

export const dashboardPersonSchema = z.object({
  displayName: z.string(),
  designation: z.string().nullable(),
  /** The current assignment's manager's display name — null when there is none on record (top of
   *  the org chart, or no assignment at all). */
  managerName: z.string().nullable(),
  cycleLabel: z.string(),
  /** `Employment.employeeCode` — mirrors `employee.contract.ts`'s `employeeSchema.employeeCode`. */
  employeeCode: z.string(),
  /** `Person.photoUrl` — mirrors `employee.contract.ts`'s `employeeSchema.photoUrl`, already
   *  consumed by `AppShell.tsx`'s `Avatar`. */
  photoUrl: z.string().url().nullable(),
});

export const dashboardScoresSchema = z.object({
  overall: ratingValueSchema,
  ratingLabel: z.string(),
  deltaVsLastCycle: signedScoreDeltaSchema.nullable(),
  scaleMax: z.number().int().positive(),
});

/**
 * One point on the score trend — one per scored closed cycle, oldest to newest on the wire (see
 * `scoreHistory` below). `cycleLabel` is not unique (labels can repeat across legal entities or
 * re-runs), so `occurredAt` is what actually orders the series; `reviewId` lets the client link a
 * point back to its review.
 */
export const dashboardScorePointSchema = z.object({
  reviewId: uuidSchema,
  cycleLabel: z.string(),
  occurredAt: instantSchema,
  score: ratingValueSchema,
  scaleMax: z.number().int().positive(),
});

export const dashboardGoalRollupSchema = z.object({
  total: z.number().int().nonnegative(),
  onTrack: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  atRisk: z.number().int().nonnegative(),
  realisationPercent: percentSchema,
});

export const dashboardAppraisalSchema = z.object({
  reviewId: uuidSchema,
  stage: z.string(),
  stageIndex: z.number().int().nonnegative(),
  stageCount: z.number().int().positive(),
  percentComplete: percentSchema,
  dueInDays: z.number().int().nullable(),
  /** The same calendar date `dueInDays` is computed from, as a plain YYYY-MM-DD — for an absolute
   *  "Next review: 28 Dec 2025"-style label rather than only a relative day count. */
  dueOn: dateOnlySchema.nullable(),
});

export const dashboardGrowthSchema = z.object({
  quarterlyDeltaPercent: signedPercentSchema.nullable(),
});

export const dashboardRecognitionSchema = z.object({
  pointsBalance: z.number().int().nonnegative(),
  recentCount: z.number().int().nonnegative(),
  latest: z.array(recognitionSchema),
});

export const dashboardBlocksSchema = z.object({
  goalsVisible: z.boolean(),
  reviewVisible: z.boolean(),
  feedbackVisible: z.boolean(),
  recognitionVisible: z.boolean(),
});

export const dashboardOverviewSchema = z.object({
  person: dashboardPersonSchema,
  scores: dashboardScoresSchema.nullable(),
  goalRollup: dashboardGoalRollupSchema.nullable(),
  appraisal: dashboardAppraisalSchema.nullable(),
  growth: dashboardGrowthSchema.nullable(),
  recognition: dashboardRecognitionSchema.nullable(),
  /** Scored closed cycles, oldest → newest. Gated on `blocks.reviewVisible` exactly like `scores`
   *  and `growth` — `null` means "you may not see this block", `[]` means "visible, but nothing
   *  scored yet". */
  scoreHistory: z.array(dashboardScorePointSchema).nullable(),
  blocks: dashboardBlocksSchema,
  /** The most recent review regardless of stage/status — independent of `appraisal`, which is
   *  null once a review closes (there is no more "current stage" to track). "Download Summary
   *  PDF" needs the latest review even when it's finished, which is exactly when a summary is
   *  most wanted. */
  latestReviewId: uuidSchema.nullable(),
});
export type DashboardOverview = z.infer<typeof dashboardOverviewSchema>;

export const getOverviewQuerySchema = z.object({
  personId: uuidSchema.optional(),
});

// ---------------------------------------------------------------------------
// getPendingActions
// ---------------------------------------------------------------------------

export const pendingActionKindSchema = z.enum([
  'workflow_step',
  'feedback_request',
  'one_on_one',
  'action_item',
  'review_stage',
]);

export const pendingActionSeveritySchema = z.enum(['info', 'warning', 'critical']);

export const pendingActionSchema = z.object({
  kind: pendingActionKindSchema,
  title: z.string(),
  subtitle: z.string().nullable(),
  dueAt: instantSchema.nullable(),
  severity: pendingActionSeveritySchema,
  /** A relative in-app route, e.g. "/performance/reviews/:reviewId" — resolved client-side. */
  deepLink: z.string(),
});
export type PendingAction = z.infer<typeof pendingActionSchema>;

export const getPendingActionsQuerySchema = paginationQuerySchema.extend({
  personId: uuidSchema.optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const performanceDashboardContract = c.router(
  {
    getOverview: {
      method: 'GET',
      path: '/overview',
      query: getOverviewQuerySchema,
      responses: {
        200: dashboardOverviewSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Aggregated dashboard overview for the caller (or a visible report) — review:read top-level gate, per-block visibility in `blocks`',
      metadata: routeMeta('review:read'),
    },

    getPendingActions: {
      method: 'GET',
      path: '/pending-actions',
      query: getPendingActionsQuerySchema,
      responses: {
        200: paginatedSchema(pendingActionSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Normalized pending-action feed across workflow steps, feedback requests, 1:1s, and review stages',
      metadata: routeMeta('review:read'),
    },
  },
  { pathPrefix: '/performance/dashboard' },
);
